import { translateOptional } from "@/i18n/translate";
import { NEUTRAL, STATUS } from "../../palette";
import type { BbKind, BbReason, BbStep } from "../../types";
import type { Tone } from "../pipeline/helpers";

/** Helpers of the BiteBerry tab (no components here). */

export const BB_KINDS: BbKind[] = ["order", "item", "status"];

/** outcome codes of the backend (monitoring/bb_webhooks.py), problems coloured */
const OUTCOME_TONE: Record<string, Tone> = {
  no_order: "nok",
  branch_not_found: "nok",
  no_camera: "nok",
  trigger_failed: "nok",
  device_error: "nok",
  error: "nok",
  no_packing_step: "warn",
  bb_trigger_disabled: "warn",
  acquisition_disabled: "warn",
  no_pizzas: "warn",
  not_pizza: "warn",
};

export const outcomeTone = (outcome: string | null | undefined): Tone =>
  (outcome && OUTCOME_TONE[outcome]) || "neutral";

/** outcomes the "problems only" filter of the log asks for */
export const PROBLEM_OUTCOMES = Object.keys(OUTCOME_TONE);

/** every outcome the log can be filtered by, in the order of the flow */
export const OUTCOMES_BY_KIND: Record<BbKind, string[]> = {
  order: ["stored", "no_pizzas", "acquisition_disabled", "error"],
  item: [
    "in_progress", "no_packing_step", "captured", "no_order", "branch_not_found", "acquisition_disabled",
    "bb_trigger_disabled", "no_camera", "trigger_failed", "device_error", "error",
  ],
  status: ["updated", "unchanged", "error"],
};

export const ALL_OUTCOMES = Array.from(new Set(Object.values(OUTCOMES_BY_KIND).flat()));

/** label of an outcome code in the UI language (unknown codes stay as they are) */
export const outcomeLabel = (outcome: string | null | undefined): string =>
  (outcome && translateOptional(`monitoring.bb.outcome.${outcome}`)) || outcome || "–";

/** item outcomes folded into the groups of the outcome chart */
export type OutcomeGroup = "captured" | "in_progress" | "skipped" | "no_order" | "failed";

export const OUTCOME_GROUPS: OutcomeGroup[] = ["captured", "in_progress", "skipped", "no_order", "failed"];

export const outcomeGroup = (outcome: string): OutcomeGroup => {
  if (outcome === "captured") return "captured";
  if (outcome === "in_progress") return "in_progress";
  if (outcome === "no_order") return "no_order";
  if (["no_packing_step", "bb_trigger_disabled", "acquisition_disabled", "not_pizza", "other_branch"].includes(outcome))
    return "skipped";
  return "failed";
};

/** calm grey for the normal flow, status colours for what went wrong */
export const GROUP_COLORS: Record<OutcomeGroup, string> = {
  captured: NEUTRAL.mid,
  in_progress: NEUTRAL.soft,
  skipped: STATUS.warning,
  no_order: STATUS.critical,
  failed: STATUS.serious,
};

/** how bad a reason of a finished pizza without a photo is */
export const REASON_TONE: Record<BbReason, Tone> = {
  capture_time_lost: "warn",
  split_item: "nok",
  no_order: "nok",
  not_pizza: "nok",
  branch_not_found: "nok",
  acquisition_disabled: "warn",
  bb_trigger_disabled: "warn",
  no_camera: "nok",
  trigger_failed: "nok",
  device_error: "nok",
  error: "nok",
  capture_not_stored: "nok",
  packed_not_logged: "warn",
  never_packed: "nok",
  no_item_webhook: "nok",
};

export const reasonLabel = (reason: string): string =>
  translateOptional(`monitoring.bb.reason.${reason}`) ?? reason;

export const reasonHint = (reason: string): string | undefined =>
  translateOptional(`monitoring.bb.reason.${reason}.hint`);

/** "Vaření 0 · Balení 1" (BiteBerry step names as they come) */
export const stepsText = (steps: BbStep[] | null | undefined): string =>
  steps?.length ? steps.map((s) => `${s.name ?? "?"} ${s.quantity_left ?? "?"}`).join(" · ") : "–";

/** BB's line id of a split piece ("X:1" -> "X") */
export const baseItemId = (id: string) => id.split(":")[0];
